/**
 * Rewrites every `id` under an SVG root and updates same-document `url(#id)` / fragment
 * references so multiple icon instances do not share duplicate ids in the HTML document.
 *
 * Covers attributes used by SVG icons (filters, gradients, clip paths, masks, markers, etc.)
 * and `style` text; does not attempt to parse arbitrary CSS beyond `url(#...)`.
 */

const FUNC_IRI_ATTRIBUTES = [
  'clip-path',
  'color-profile',
  'cursor',
  'fill',
  'filter',
  'marker',
  'marker-start',
  'marker-mid',
  'marker-end',
  'mask',
  'stroke',
  'src',
] as const;

let instanceSeq = 0;

function nextPrefix(): string {
  instanceSeq += 1;
  const n =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '')
      : `${Date.now().toString(36)}_${instanceSeq}`;
  return `rvi_${n}_`;
}

function replaceUrlFunciris(
  value: string,
  pairs: readonly { oldId: string; newId: string }[],
): string {
  let v = value;
  for (const { oldId, newId } of pairs) {
    v = v.replaceAll(`url(#${oldId})`, `url(#${newId})`);
    v = v.replaceAll(`url('#${oldId}')`, `url(#${newId})`);
    v = v.replaceAll(`url("#${oldId}")`, `url(#${newId})`);
  }
  return v;
}

function rewriteElementAttributes(
  el: Element,
  pairs: readonly { oldId: string; newId: string }[],
): void {
  for (const attr of FUNC_IRI_ATTRIBUTES) {
    const val = el.getAttribute(attr);
    if (val) {
      const next = replaceUrlFunciris(val, pairs);
      if (next !== val) {
        el.setAttribute(attr, next);
      }
    }
  }

  for (let i = 0; i < el.attributes.length; i++) {
    const attr = el.attributes[i];
    if (!attr.value.includes('url(#')) {
      continue;
    }
    if (
      FUNC_IRI_ATTRIBUTES.includes(
        attr.name as (typeof FUNC_IRI_ATTRIBUTES)[number],
      )
    ) {
      continue;
    }
    const next = replaceUrlFunciris(attr.value, pairs);
    if (next !== attr.value) {
      el.setAttribute(attr.name, next);
    }
  }

  const href = el.getAttribute('href');
  if (href?.startsWith('#')) {
    const frag = href.slice(1);
    const hit = pairs.find((p) => p.oldId === frag);
    if (hit) {
      el.setAttribute('href', `#${hit.newId}`);
    }
  }

  const xlink = el.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
  if (xlink?.startsWith('#')) {
    const frag = xlink.slice(1);
    const hit = pairs.find((p) => p.oldId === frag);
    if (hit) {
      el.setAttributeNS(
        'http://www.w3.org/1999/xlink',
        'href',
        `#${hit.newId}`,
      );
    }
  }

  const style = el.getAttribute('style');
  if (style) {
    const next = replaceUrlFunciris(style, pairs);
    if (next !== style) {
      el.setAttribute('style', next);
    }
  }
}

/**
 * Call on each `<svg>` returned for display (e.g. before inserting into the DOM).
 */
export function scopeSvgFragmentIds(root: SVGElement): void {
  const withId: Element[] = root.hasAttribute('id')
    ? [root, ...Array.from(root.querySelectorAll('[id]'))]
    : Array.from(root.querySelectorAll('[id]'));
  if (!withId.length) {
    return;
  }
  if (!withId.length) {
    return;
  }

  const prefix = nextPrefix();
  const pairs: { el: Element; oldId: string; newId: string }[] = [];

  for (let i = 0; i < withId.length; i++) {
    const el = withId[i];
    const oldId = el.getAttribute('id');
    if (!oldId) {
      continue;
    }
    pairs.push({ el, oldId, newId: `${prefix}${oldId}` });
  }

  pairs.sort((a, b) => b.oldId.length - a.oldId.length);

  const mapOnly = pairs.map(({ oldId, newId }) => ({ oldId, newId }));

  for (const { el, newId } of pairs) {
    el.setAttribute('id', newId);
  }

  const subtree: Element[] = [root, ...Array.from(root.querySelectorAll('*'))];
  for (const el of subtree) {
    rewriteElementAttributes(el, mapOnly);
  }

  const styleBlocks = root.querySelectorAll('style');
  for (let i = 0; i < styleBlocks.length; i++) {
    const node = styleBlocks[i];
    const text = node.textContent;
    if (text) {
      const next = replaceUrlFunciris(text, mapOnly);
      if (next !== text) {
        node.textContent = next;
      }
    }
  }
}
